import React from 'react';
import { Link } from 'react-router-dom';
import { getBlogPostById, getBlogPostBySlug } from '../data/blogPosts';

interface BlogPostViewerProps {
  postId: string;
  darkMode?: boolean;
}

const BlogPostViewer: React.FC<BlogPostViewerProps> = ({ postId, darkMode = false }) => {
  // Try to find by slug first, then by numeric ID for backward compatibility
  let post = getBlogPostBySlug(postId);
  if (!post) {
    post = getBlogPostById(postId);
  }

  if (!post) {
    return (
      <div className={`min-h-screen py-20 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className={`text-4xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Blog Post Not Found
            </h1>
            <p className={`text-xl mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              The blog post you're looking for doesn't exist.
            </p>
            <Link 
              to="/" 
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let listKey = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${listKey++}`} className={`space-y-3 mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {currentList.map((item, index) => (
              <li key={index} className="flex items-start">
                <div className={`w-2 h-2 rounded-full mt-3 mr-4 flex-shrink-0 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></div>
                <div className="flex-1 leading-relaxed">
                  {formatInlineText(item)}
                </div>
              </li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const formatInlineText = (text: string) => {
      // Handle math step formatting
      if (text.startsWith('<math-step>') && text.endsWith('</math-step>')) {
        const mathContent = text.replace('<math-step>', '').replace('</math-step>', '');
        return (
          <div className={`p-6 rounded-xl border-l-4 my-6 ${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-400'}`}>
            <div className="font-mono text-sm leading-relaxed">
              {mathContent}
            </div>
          </div>
        );
      }

      // Handle bold text with **text** pattern
      const boldRegex = /\*\*(.*?)\*\*/g;
      const parts = text.split(boldRegex);
      
      return parts.map((part, index) => {
        if (index % 2 === 1) {
          // This is a bold part
          return (
            <strong key={index} className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {part}
            </strong>
          );
        }
        
        // Handle code formatting with backticks
        if (part.includes('`')) {
          const codeParts = part.split(/(`.*?`)/g);
          return codeParts.map((codePart, codeIndex) => {
            if (codePart.startsWith('`') && codePart.endsWith('`')) {
              return (
                <code key={`${index}-${codeIndex}`} className={`px-2 py-1 rounded text-sm font-mono ${darkMode ? 'bg-gray-700 text-blue-300' : 'bg-gray-100 text-blue-600'}`}>
                  {codePart.replace(/`/g, '')}
                </code>
              );
            }
            return codePart;
          });
        }
        
        return part;
      });
    };

    lines.forEach((line, index) => {
      if (line.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className={`text-3xl font-bold mt-16 mb-8 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className={`text-2xl font-bold mt-12 mb-6 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {line.replace('### ', '')}
          </h3>
        );
      } else if ((line.startsWith('**') && line.endsWith('**') && !line.includes('**')) || line.match(/^\*\*[^*]+\*\*$/)) {
        flushList();
        elements.push(
          <p key={index} className={`text-xl font-semibold mb-8 leading-relaxed ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else if (line.startsWith('* ')) {
        // Regular bullet point - add to list
        const content = line.replace('* ', '');
        currentList.push(content);
      } else if (line.startsWith('---')) {
        flushList();
        elements.push(<hr key={index} className={`my-12 ${darkMode ? 'border-gray-700' : 'border-gray-300'}`} />);
      } else if (line.trim() === '') {
        flushList();
        elements.push(<br key={index} />);
      } else if (line.includes('**Ready to') || line.includes('**Don\'t leave') || line.includes('**Stop wasting') || line.includes('**Help a future') || line.includes('**Don\'t wait')) {
        flushList();
        elements.push(
          <div key={index} className={`mt-12 p-8 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700' : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'}`}>
            <p className={`text-xl font-semibold leading-relaxed ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              {formatInlineText(line)}
            </p>
          </div>
        );
      } else if (line.trim() !== '') {
        flushList();
        elements.push(
          <p key={index} className={`mb-6 leading-relaxed text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {formatInlineText(line)}
          </p>
        );
      }
    });

    flushList(); // Flush any remaining list items
    return elements;
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Hero Section */}
      <div className={`relative ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} py-20`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link 
            to="/resources" 
            className={`inline-flex items-center mb-8 text-sm font-medium hover:underline transition-colors ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Resources
          </Link>

          {/* Category Badge */}
          <div className="mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
              post.category === 'Exam Prep' ? 'bg-blue-100 text-blue-800' :
              post.category === 'Study Guide' ? 'bg-green-100 text-green-800' :
              post.category === 'Career Advice' ? 'bg-purple-100 text-purple-800' :
              post.category === 'Licensing Guide' ? 'bg-orange-100 text-orange-800' :
              post.category === 'Study Tips' ? 'bg-indigo-100 text-indigo-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className={`text-5xl lg:text-6xl font-bold mb-8 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {post.title}
          </h1>

          {/* Excerpt */}
          <p className={`text-xl lg:text-2xl mb-8 leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {post.excerpt}
          </p>

          {/* Meta Information */}
          <div className="flex items-center space-x-6">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-white shadow-lg'}`}>
              <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                {post.author.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {post.author}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {new Date(post.date).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })} • {post.readTime}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          {formatContent(post.content || '')}
        </div>

        {/* CTA Section */}
        <div className={`mt-20 p-10 rounded-3xl ${darkMode ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700' : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'}`}>
          <div className="text-center">
            <h3 className={`text-3xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Ready to Start Your Real Estate Journey?
            </h3>
            <p className={`text-xl mb-8 leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Join thousands of students who are already using RealEstart to prepare for their exam.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="/demo"
                className="bg-blue-600 text-white px-10 py-4 rounded-full font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl text-lg"
              >
                Try Demo - No Registration Required
              </a>
              <a
                href="/checkout?plan=trial"
                className="border-2 border-blue-600 text-blue-600 px-10 py-4 rounded-full font-semibold hover:bg-blue-600 hover:text-white transition-all transform hover:scale-105 text-lg"
              >
                Start 3-Day Free Trial
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostViewer;